/**
 * Auto-detect video provider from a pasted URL. The client routine
 * player (RoutineSection.tsx) supports four providers; the coach's
 * catalog form pastes the URL and the back-end stores both the URL
 * and the matching type so the player knows how to embed.
 */

export type VideoMediaType = 'youtube' | 'vimeo' | 'instagram' | 'supabase';

export function detectMediaType(url: string): VideoMediaType | null {
  const u = (url || '').trim().toLowerCase();
  if (!u) return null;

  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('vimeo.com')) return 'vimeo';
  if (u.includes('instagram.com')) return 'instagram';
  // Supabase Storage URLs are passed as keys/paths, not full URLs.
  // Don't try to auto-detect them — coach uploads via a separate flow.
  return null;
}

/** Human-readable name for a media type, used in tooltips. */
export function mediaTypeLabel(t: VideoMediaType | null | undefined): string {
  switch (t) {
    case 'youtube': return 'YouTube';
    case 'vimeo': return 'Vimeo';
    case 'instagram': return 'Instagram';
    case 'supabase': return 'Video subido';
    default: return 'Video';
  }
}

/**
 * Extract the 11-char YouTube video ID from any of the four URL forms
 * coaches paste:
 *   - https://www.youtube.com/watch?v=ID
 *   - https://youtu.be/ID
 *   - https://www.youtube.com/embed/ID
 *   - https://www.youtube.com/shorts/ID    ← was missing, broke thumbnails
 *
 * Returns null when the URL doesn't match any of the above.
 */
export function youtubeId(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/))([A-Za-z0-9_-]{11})/,
  );
  return m?.[1] ?? null;
}

/** Build the canonical hqdefault thumbnail URL, or null. */
export function youtubeThumbnail(url: string | null | undefined): string | null {
  const id = youtubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

/**
 * Devuelve el set ordenado de URLs de thumbnail de un video de YouTube,
 * de mejor a peor calidad. Permite probar `maxresdefault` primero y caer
 * en cascada a `hqdefault → mqdefault → default` si las superiores no
 * existen para ese video. Muchos videos viejos solo tienen las versiones
 * pequeñas y el `<img>` se quedaba sin imagen.
 *
 * Uso con ResilientImage:
 *   <ResilientImage src={...} fallbackSrcs={youtubeThumbnailFallbacks(url)} />
 */
export function youtubeThumbnailFallbacks(url: string | null | undefined): string[] {
  const id = youtubeId(url);
  if (!id) return [];
  // OJO: maxresdefault.jpg NO se usa como primario a propósito.
  // Cuando un video no tiene esa resolución, YouTube NO devuelve 404
  // — sirve una imagen gris de 120x90 con status 200. El onError del
  // <img> nunca se dispara, la cascada de fallbacks no avanza, y el
  // card se queda con un thumbnail gris vacío ("algunos videos no se
  // ven"). hqdefault.jpg SIEMPRE existe para todo video válido, así
  // que es el primario seguro. maxres queda al final como bonus
  // opcional (si por azar hq fallara, cosa que casi nunca pasa).
  return [
    `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
    `https://img.youtube.com/vi/${id}/mqdefault.jpg`,
    `https://img.youtube.com/vi/${id}/sddefault.jpg`,
    `https://img.youtube.com/vi/${id}/default.jpg`,
  ];
}
